import { useEffect, useState } from 'react';
import { useUpdate } from '../../engine/api/hooks';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Field, Input, Select, Switch, Textarea } from '../../engine/design/primitives/Input';
import { Banner, EmptyState } from '../../engine/design/primitives/Misc';
import { MoneyEditor } from '../../engine/fields/registry';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import type { FieldMeta } from '../../engine/meta/types';
import type { ScreenContext } from '../../engine/screens/types';
import { INTERVALS, INTERVAL_LABEL } from './totals';

// Quote template detail tab: a structured editor for the `sections` blueprint (jsonb) — the
// sections (title, selection rule, optional flag) and their seed lines (product or free text)
// a quote created from the template starts with. Saved as one PATCH on the template.

interface BpLine {
  product_id?: string | null;
  description?: string;
  qty?: string | number;
  unit_price_minor?: string | number;
  unit_cost_minor?: string | number;
  kind?: 'one_off' | 'recurring';
  recurring_interval?: string | null;
  is_optional?: boolean;
}
interface BpSection {
  title: string;
  description?: string | null;
  selection?: 'all' | 'single' | 'multi';
  is_optional?: boolean;
  lines?: BpLine[];
}

const PRODUCT_FIELD: FieldMeta = { name: 'product_id', label: 'Product', type: 'relation', required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: false, inDetail: false, relation: { resource: 'products', displayField: 'name' } };
const PRICE_FIELD: FieldMeta = { ...PRODUCT_FIELD, name: 'unit_price_minor', label: 'Unit price', type: 'money', relation: undefined, currencyField: 'currency' };

export function TemplateBlueprintTab({ id, row, refresh }: ScreenContext) {
  const [sections, setSections] = useState<BpSection[]>(() => (Array.isArray(row?.sections) ? (row!.sections as BpSection[]) : []));
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) setSections(Array.isArray(row?.sections) ? (row!.sections as BpSection[]) : []);
  }, [row, dirty]);
  const update = useUpdate('quote-templates', { toast: { success: 'Blueprint saved' }, onSuccess: () => { setDirty(false); refresh(); } });
  const set = (next: BpSection[]) => {
    setSections(next);
    setDirty(true);
  };
  const patchSection = (i: number, p: Partial<BpSection>) => set(sections.map((s, k) => (k === i ? { ...s, ...p } : s)));
  const patchLine = (i: number, j: number, p: Partial<BpLine>) => patchSection(i, { lines: (sections[i]!.lines ?? []).map((l, k) => (k === j ? { ...l, ...p } : l)) });

  return (
    <div className="hf-stack" style={{ maxWidth: 960 }}>
      <Banner tone="info">Quotes created from this template start with these sections and lines. Product lines take their price from the client’s price book at quote time; free-text lines keep the price entered here.</Banner>
      {sections.length === 0 && <EmptyState title="No sections yet" hint="Add a section to seed every quote made from this template." icon="layers" />}
      {sections.map((s, i) => (
        <section key={i} className="hf-card pad hf-stack" aria-label={`Section ${i + 1}`}>
          <div className="hf-form-grid">
            <Field label="Section title">
              <Input value={s.title} onChange={(e) => patchSection(i, { title: e.target.value })} />
            </Field>
            <Field label="Selection rule">
              <Select value={s.selection ?? 'all'} onChange={(e) => patchSection(i, { selection: e.target.value as BpSection['selection'] })} options={[{ value: 'all', label: 'All lines (optional lines can be dropped)' }, { value: 'single', label: 'Pick exactly one' }, { value: 'multi', label: 'Pick any' }]} />
            </Field>
            <Field label="Description" className="span-2">
              <Textarea rows={2} value={s.description ?? ''} onChange={(e) => patchSection(i, { description: e.target.value })} />
            </Field>
            <Switch label="Whole section is optional" checked={Boolean(s.is_optional)} onChange={(e) => patchSection(i, { is_optional: e.target.checked })} />
          </div>
          <div className="hf-stack" style={{ gap: 8 }}>
            {(s.lines ?? []).map((l, j) => (
              <div key={j} className="hf-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220, flex: 2 }}>
                  <Field label="Product (optional)">
                    <RelationPicker field={PRODUCT_FIELD} value={l.product_id ?? null} onChange={(v, label) => patchLine(i, j, { product_id: v, description: v ? (label ?? l.description) : l.description })} size="sm" />
                  </Field>
                </div>
                <div style={{ minWidth: 200, flex: 3 }}>
                  <Field label="Description">
                    <Input size="sm" value={l.description ?? ''} onChange={(e) => patchLine(i, j, { description: e.target.value })} />
                  </Field>
                </div>
                <div style={{ width: 80 }}>
                  <Field label="Qty">
                    <Input size="sm" numeric value={String(l.qty ?? 1)} onChange={(e) => patchLine(i, j, { qty: e.target.value })} />
                  </Field>
                </div>
                {!l.product_id && (
                  <div style={{ width: 140 }}>
                    <Field label="Unit price">
                      <MoneyEditor field={PRICE_FIELD} value={l.unit_price_minor ?? null} onChange={(v) => patchLine(i, j, { unit_price_minor: v == null ? undefined : String(v) })} size="sm" currency={String(row?.currency ?? 'USD')} />
                    </Field>
                  </div>
                )}
                {!l.product_id && (
                  <div style={{ width: 150 }}>
                    <Field label="Billing">
                      <Select size="sm" value={l.kind === 'recurring' ? (l.recurring_interval ?? 'monthly') : 'one_off'} onChange={(e) => patchLine(i, j, e.target.value === 'one_off' ? { kind: 'one_off', recurring_interval: null } : { kind: 'recurring', recurring_interval: e.target.value })} options={[{ value: 'one_off', label: 'One-time' }, ...INTERVALS.map((iv) => ({ value: iv, label: `Recurring ${INTERVAL_LABEL[iv]}` }))]} />
                    </Field>
                  </div>
                )}
                <Switch label="Optional" checked={Boolean(l.is_optional)} onChange={(e) => patchLine(i, j, { is_optional: e.target.checked })} />
                <IconButton icon="trash" label="Remove line" variant="ghost" size="sm" onClick={() => patchSection(i, { lines: (s.lines ?? []).filter((_, k) => k !== j) })} />
              </div>
            ))}
            <div className="hf-row">
              <Button size="sm" icon="plus" onClick={() => patchSection(i, { lines: [...(s.lines ?? []), { description: '', qty: 1, kind: 'one_off' }] })}>Add line</Button>
              <span className="hf-grow" />
              <Button size="sm" variant="ghost" icon="trash" onClick={() => set(sections.filter((_, k) => k !== i))}>Remove section</Button>
            </div>
          </div>
        </section>
      ))}
      <div className="hf-row">
        <Button icon="plus" onClick={() => set([...sections, { title: `Section ${sections.length + 1}`, selection: 'all', is_optional: false, lines: [] }])}>Add section</Button>
        <span className="hf-grow" />
        <Button variant="primary" disabled={!dirty} busy={update.isPending} onClick={() => update.mutate({ id: id!, body: { sections } })}>Save blueprint</Button>
      </div>
    </div>
  );
}
